import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { first, map, takeUntil } from "rxjs/operators";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { SortEnum } from "../column-filter/column-filter.component";
import { ColumnDefinition } from "../Models/buildingTableViewModel";
import { ColumnTypes } from "../Models/enums/ColumnTypeEnum";
import {
  orderedSurveyStatus,
  SurveyStatus,
} from "../Models/enums/SurveySurveyStatus";
import { ToastService } from "../toast.service";

@Component({
  template: ``,
})
export class FilterableTableComponent<T> implements OnInit, OnDestroy {
  tableValues: T[] = [];
  cols: ColumnDefinition<T>[] = [];
  allCols: ColumnDefinition<T>[] = [];
  globalFilter$ = new BehaviorSubject<string>("");
  columnFilters$ = new BehaviorSubject<{ [field: string]: any[] }>({});
  sortFields$ = new BehaviorSubject<{ [field: string]: boolean }>({});
  tableData$ = (doSort: boolean = true, ...excludeHeaders: string[]) =>
    combineLatest([
      this.globalFilter$,
      this.columnFilters$,
      this.sortFields$,
    ]).pipe(
      map(([globalFilter, columnFilters, sort]) => {
        let filtered = [
          ...this.tableValues.filter((m) =>
            this.cols.some((col) => {
              let val = col?.transform?.(m) ?? this.getField(m, col.field);
              if (col.field == "surveyStatus") val = SurveyStatus[val];

              return val
                ?.toString()
                .toLocaleLowerCase()
                .includes(globalFilter.toLocaleLowerCase());
            })
          ),
        ];
        filtered = filtered.filter((m) =>
          Object.entries(columnFilters).every(([key, values]) => {
            if (excludeHeaders.includes(key)) return true;
            if (values.length == 0) return false;
            const col = this.cols.find((c) => c.field == key);
            if (!col) return true;
            const val = col?.transform
              ? col.transform(m)
              : this.getField(m, key);

            if (
              col.field == "numberOfLines" ||
              col.field == "numberOfSkippedLines" ||
              col.field == "numberOfSuccessfulLines" ||
              col.field == "numberOfErrors"
            )
              return values.some((v) => (!!v ? !!val : v == val));
            else return values.some((v) => (!v ? !val : v == val));
          })
        );
        if (doSort)
          filtered.sort((a, b) => {
            let result = 0;
            for (let field of Object.keys(sort)) {
              const reverse = !sort[field] ? 1 : -1;
              // const first = this.getField(a, field);
              // const second = this.getField(b, field);
              const first =
                field === "surveyStatus"
                  ? this.substituteStatus(this.getField(a, field))
                  : this.getField(a, field);
              const second =
                field === "surveyStatus"
                  ? this.substituteStatus(this.getField(b, field))
                  : this.getField(b, field);
              const col = this.cols.find((c) => c.field == field);
              if (first == undefined && second == undefined) result ||= 0;
              else if (first == undefined) result ||= reverse;
              else if (second == undefined) result ||= -reverse;
              else
                switch (col?.type) {
                  case ColumnTypes.BOOLEAN:
                    result ||= reverse * (first && !second ? -1 : 1);
                    break;
                  case ColumnTypes.NUMBER:
                    result ||= reverse * (first - second);
                    break;
                  case ColumnTypes.DATE:
                    result ||=
                      reverse *
                      (new Date(first).getTime() - new Date(second).getTime());
                    break;
                  default:
                    result ||= reverse * first.localeCompare(second);
                    break;
                }
            }
            return result;
          });
        return filtered;
      })
    );

  extendedFilterActive$ = combineLatest([
    this.globalFilter$,
    this.columnFilters$,
    this.sortFields$,
  ]).pipe(
    map(
      ([global, filters, sort]) =>
        !!global || Object.keys(filters).length || Object.keys(sort).length
    )
  );

  filterValue: string = "";

  get globalFilter() {
    return this.filterValue;
  }

  set globalFilter(value: string) {
    this.onFilterTable(value);
    this.filterValue = value;
  }

  destroy$ = new Subject();

  constructor(
    protected toastService: ToastService,
    protected deviceCheckService: DeviceCheckService
  ) {}

  ngOnInit() {
    this.cols = this.deviceCheckService.widthCheck(1001)
      ? this.allCols.filter((x) => x.visibleOnPortrait)
      : this.allCols;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  @HostListener("window:resize")
  onResize() {
    this.cols = this.deviceCheckService.widthCheck(1001)
      ? this.allCols.filter((x) => x.visibleOnPortrait)
      : this.allCols;
    this.globalFilter$.next(this.globalFilter);
  }

  initPersistence(key: string) {
    let val = sessionStorage.getItem(key);
    if (val) {
      let data = JSON.parse(val);
      this.globalFilter = data.globalFilter;
      this.columnFilters$.next(data.columnFilters);
      this.sortFields$.next(data.sortFields);
    }
    combineLatest([this.globalFilter$, this.columnFilters$, this.sortFields$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([globalFilter, columnFilters, sortFields]) =>
        sessionStorage.setItem(
          key,
          JSON.stringify({
            globalFilter,
            columnFilters,
            sortFields,
          })
        )
      );
  }

  checkDataField(fieldName: string): boolean {
    return this.cols.some((x) => x.field == fieldName);
  }
  checkSortIcon(fieldName: string): boolean {
    return this.allCols.some((x) => x.field == fieldName && x.hasFilter);
  }

  onFilterTable(value: string) {
    this.globalFilter$.next(value);
  }
  clearAllFilter() {
    this.globalFilter = "";
    this.columnFilters$.next({});
    this.sortFields$.next({});
  }
  setSort(fieldName: string, sortOrder: SortEnum): void {
    this.sortFields$.pipe(first()).subscribe((sort) => {
      if (sortOrder == SortEnum.NONE) delete sort[fieldName];
      else sort[fieldName] = sortOrder == SortEnum.ASCENDING;
      this.sortFields$.next(sort);
    });
  }

  getField(obj: any, header: string) {
    var split = header.split(".");
    if (!split.length) return undefined;
    var result = obj;
    for (let f of split) {
      if (result == null || result == undefined) return undefined;
      result = result[f];
    }
    return result;
  }
  substituteStatus(status: number) {
    // dirty workaround to fix status sorting
    return orderedSurveyStatus.findIndex((orderedStatus) => {
      return orderedStatus === status;
    });
  }
  getFieldValues$(header: string) {
    const col = this.allCols.find((c) => c.field == header);
    return this.tableData$(false, header).pipe(
      map((d) => {
        // Stupid surveystatus with incorrect ordering >:(
        if (col.field == "surveyStatus")
          return orderedSurveyStatus.filter((s) =>
            d.map((b) => b["surveyStatus"]).includes(s)
          );
        return d
          .map((b) =>
            col?.transform ? col.transform(b) : this.getField(b, header)
          )
          .filter((b, i, a) => a.findIndex((v) => v === b) === i)
          .sort((a, b) => {
            if (typeof a == "object") return 1;
            if (col.type == ColumnTypes.DATE)
              return new Date(a).getTime() - new Date(b).getTime();
            if (col.type == ColumnTypes.NUMBER) return a - b;
            if (col.type == ColumnTypes.BOOLEAN) return a && !b ? -1 : 1;
            return a.localeCompare(b);
          });
      })
    );
  }
  getFieldTemplate(header: string) {
    const temp = this.allCols.find((c) => c.field == header).template;
    return temp == "default" ? undefined : this[temp + "Template"];
  }
  getFieldFilterSearch(header: string) {
    return this.allCols.find((c) => c.field == header).filterSearch;
  }
  getFieldHasEmptyValue(header: string) {
    return this.allCols.find((c) => c.field == header).hasEmptyValue;
  }

  setFilter(header: string, filter: any[]) {
    this.columnFilters$.pipe(first()).subscribe((f) => {
      if (filter == undefined) delete f[header];
      else f[header] = filter;
      this.columnFilters$.next(f);
    });
  }
  getFieldFilters$(header: string) {
    return this.columnFilters$.pipe(
      first(),
      map((f) => f[header])
    );
  }
  clearFieldFilters(header: string) {
    this.columnFilters$.pipe(first()).subscribe((f) => {
      delete f[header];
      this.columnFilters$.next(f);
    });
  }
  getFieldSort$(header: string) {
    return this.sortFields$.pipe(
      map((s) =>
        s[header] != undefined
          ? s[header]
            ? SortEnum.ASCENDING
            : SortEnum.DESCENDING
          : SortEnum.NONE
      )
    );
  }

  scrollOffsetLandscape: number = 0;
  scrollOffsetPortrait: number = 0;

  getScrollHeight() {
    if (!this.deviceCheckService.widthCheck(1000))
      return window.innerHeight - this.scrollOffsetLandscape + "px";
    else return window.innerHeight - this.scrollOffsetPortrait + "px";
  }
}
